import { useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  APP_VERSION,
  ENV,
  IS_DEVELOPMENT,
  SENTRY_DSN,
  SENTRY_REPLAY_SESSION_SAMPLE_RATE,
  SENTRY_TRACES_SAMPLE_RATE
} from '../lib/env';

const sentryDsn = SENTRY_DSN;

// E2 (soft launch v3): in staging/production, missing Sentry DSN is a
// startup error — running blind in prod means we can't see the bug
// reports our customers are filing through the support chat. We still
// allow boot (so the app is at least useful), but we surface a banner
// via `getSentryStartupBanner()` consumed by `AppProviders` below, and
// emit a `console.error` that Sentry's own beacon would catch *if* it
// were configured (so this never silently regresses to "warn-and-go").
const isNonDevRuntime = ENV.mode === 'production' || ENV.mode === 'staging';
let sentryStartupError: string | null = null;
if (!sentryDsn) {
  if (isNonDevRuntime) {
    sentryStartupError =
      `[Sentry] VITE_SENTRY_DSN is required in ${ENV.mode}; the app is running blind to client-side errors. Ship a build with the DSN set.`;
    console.error(sentryStartupError);
  } else {
    console.warn('[Sentry] VITE_SENTRY_DSN not set; Sentry events disabled');
  }
}

const getSentryStartupBanner = (): string | null => sentryStartupError;

const readAnalyticsConsent = (): boolean => {
  try {
    return window.localStorage.getItem('ninpo-cookie-consent.v1') === 'accepted';
  } catch {
    return false;
  }
};

const tracesSampleRate = SENTRY_TRACES_SAMPLE_RATE;
const replaySessionSampleRate = SENTRY_REPLAY_SESSION_SAMPLE_RATE;
const analyticsConsented = readAnalyticsConsent();

if (sentryDsn && !IS_DEVELOPMENT) {
  Sentry.init({
    dsn: sentryDsn,
    environment: ENV.mode,
    release: APP_VERSION,
    sendDefaultPii: false,
    // Always track errors, but only trace/replay if consented.
    tracesSampleRate: analyticsConsented ? tracesSampleRate : 0,
    replaysSessionSampleRate: analyticsConsented ? replaySessionSampleRate : 0,
    replaysOnErrorSampleRate: analyticsConsented ? 1.0 : 0,
    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false,
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network request failed',
      'Failed to fetch',
      'Authentication required',
      'Token expired',
      'Cart has changed. Refresh and try again.',
      /^ApiError: Authentication required$/,
      /^ApiError: Token expired$/,
      /^ApiError: Cart has changed/,
    ],
    initialScope: {
      tags: {
        app: 'ninpo-snacks',
        component: 'frontend',
      },
    },
    beforeSend(event, hint) {
      const original = hint?.originalException as
        | { name?: string; message?: string; status?: number }
        | string
        | undefined;
      const message =
        typeof original === 'string'
          ? original
          : String(original?.message || event.message || event.exception?.values?.[0]?.value || '');
      const status = typeof original === 'object' && original ? Number(original.status || 0) : 0;
      const isExpectedClientNoise =
        /Authentication required/i.test(message) ||
        /Token expired/i.test(message) ||
        /Cart has changed/i.test(message) ||
        (typeof original === 'object' &&
          original?.name === 'ApiError' &&
          (status === 401 || status === 403 || status === 409));
      if (isExpectedClientNoise) {
        return null;
      }
      if (event.request) {
        event.request.headers = {
          ...event.request.headers,
          'User-Agent': navigator.userAgent,
        };
      }
      return event;
    },
  });

  // Raise sample rates when consent is accepted after boot (no full reload).
  if (typeof window !== 'undefined') {
    window.addEventListener('cookie-consent', ((event: Event) => {
      const choice = String((event as CustomEvent<{ choice?: string }>).detail?.choice || '');
      if (choice !== 'accepted') return;
      const client = Sentry.getClient();
      if (!client) return;
      const options = client.getOptions() as {
        tracesSampleRate?: number;
        replaysSessionSampleRate?: number;
        replaysOnErrorSampleRate?: number;
      };
      options.tracesSampleRate = tracesSampleRate;
      options.replaysSessionSampleRate = replaySessionSampleRate;
      options.replaysOnErrorSampleRate = 1.0;
    }) as EventListener);
  }
}

/**
 * D4 (hard launch v4): React Query adoption.
 *
 * Single shared client at the provider root so all `useQuery` /
 * `useMutation` hooks inherit the same cache, request dedupe, and
 * AbortSignal plumbing. Defaults are tuned for "interactive admin
 * dashboards on a laptop" — short staleTime, generous gcTime, no
 * refetch on window focus (we already have a 30s background poll
 * elsewhere), retry once with backoff for transient 5xx.
 *
 * The client is created via `useState(() => ...)` so a single
 * instance is created per browser session and never re-created on
 * re-render.
 */
const createAppQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount: number, error: unknown) => {
          if (failureCount >= 1) return false;
          // Don't retry user errors. Only retry on network/5xx-shaped errors.
          const message = String((error as { message?: string })?.message || '').toLowerCase();
          if (/4\d\d|unauthor|forbid|not[\s_-]?found/.test(message)) return false;
          return true;
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
      },
      mutations: {
        retry: 0
      }
    }
  });

export function AppProviders({ children }: { children: ReactNode }) {
  const startupError = getSentryStartupBanner();
  const [queryClient] = useState(() => createAppQueryClient());

  useEffect(() => {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
    });

    persistQueryClient({
      queryClient,
      persister,
      maxAge: 1000 * 60 * 60 * 24, // 24 hours
    });
  }, [queryClient]);

  // Re-emit on mount so the error is captured by any error-tracking layer
  // wired up later in the React tree (e.g. Datadog RUM beacon also reads
  // window.onerror), not just by the module-load-time console.error above.
  useEffect(() => {
    if (startupError && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:startup-error', { detail: { message: startupError, kind: 'sentry_dsn_missing' } }));
    }
  }, [startupError]);

  return (
    <Sentry.ErrorBoundary fallback={<div>An error occurred. Please refresh the page.</div>}>
      <QueryClientProvider client={queryClient}>
        {startupError ? (
          <div
            role="alert"
            style={{
              background: '#7f1d1d',
              color: '#fff',
              padding: '8px 12px',
              fontSize: 12,
              fontFamily: 'monospace',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 99999,
              textAlign: 'center'
            }}
          >
            {startupError}
          </div>
        ) : null}
        {children}
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
